<template>
  <div class="email-cfg">
    <!-- ── Sidebar ── -->
    <nav class="email-cfg__nav">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        :class="['email-cfg__nav-item', { active: activeTab === tab.key }]"
        @click="activeTab = tab.key"
      >
        <i :class="tab.icon"></i>
        <span>{{ tab.label }}</span>
      </button>
    </nav>

    <!-- ── Content ── -->
    <div class="email-cfg__body">
      <!-- ── SMTP ── -->
      <template v-if="activeTab === 'smtp'">
        <div class="email-cfg__header">
          <div class="email-cfg__header-info">
            <h5 class="email-cfg__title">Servidor de correo (SMTP)</h5>
            <p class="email-cfg__desc">
              Configura tu servidor SMTP para el envío de documentos. Si lo
              dejas vacío se usará el correo por defecto de Mydessk.
            </p>
          </div>
          <ButtonSaveComponent @onSaveClick="handleSaveClick" />
        </div>

        <el-form>
          <div class="ecfg-section">
            <h6 class="ecfg-section__title">Credenciales de acceso</h6>
            <b-row>
              <b-col xs="12" sm="12" md="6">
                <el-form-item class="ecfg-field">
                  <label class="ecfg-field__label"
                    >Usuario / Correo electrónico</label
                  >
                  <el-input
                    type="text"
                    name="smtp-username"
                    autocomplete="off"
                    :readonly="fieldsLocked"
                    @focus="fieldsLocked = false"
                    v-model="userConfigSmtp.email"
                    placeholder="correo@empresa.com"
                  />
                </el-form-item>
              </b-col>
              <b-col xs="12" sm="12" md="6">
                <el-form-item class="ecfg-field">
                  <label class="ecfg-field__label">Contraseña</label>
                  <el-input
                    type="password"
                    name="smtp-password"
                    autocomplete="new-password"
                    :readonly="fieldsLocked"
                    @focus="fieldsLocked = false"
                    v-model="userConfigSmtp.password"
                    show-password
                    placeholder="••••••••"
                  />
                </el-form-item>
              </b-col>
            </b-row>
          </div>

          <div class="ecfg-section">
            <h6 class="ecfg-section__title">Configuración del servidor</h6>
            <b-row>
              <b-col xs="12" sm="12" md="5">
                <el-form-item class="ecfg-field">
                  <label class="ecfg-field__label">Servidor SMTP</label>
                  <el-input
                    type="text"
                    name="smtp-host"
                    autocomplete="off"
                    :readonly="fieldsLocked"
                    @focus="fieldsLocked = false"
                    v-model="userConfigSmtp.host"
                    placeholder="smtp.gmail.com"
                  />
                  <span
                    :class="[
                      'ecfg-field__hint',
                      { 'ecfg-field__hint--error': isSmtpHostInvalid }
                    ]"
                    >{{
                      isSmtpHostInvalid
                        ? "Este campo debe ser el nombre del servidor (ej: smtp.gmail.com), no un correo electrónico."
                        : "Es el nombre del servidor (ej: smtp.gmail.com), no tu correo electrónico."
                    }}</span
                  >
                </el-form-item>
              </b-col>
              <b-col xs="12" sm="12" md="3">
                <el-form-item class="ecfg-field">
                  <label class="ecfg-field__label">Puerto</label>
                  <el-input
                    type="text"
                    name="smtp-port"
                    autocomplete="off"
                    v-model="userConfigSmtp.port"
                    placeholder="465"
                  />
                </el-form-item>
              </b-col>
              <b-col xs="12" sm="12" md="4">
                <el-form-item class="ecfg-field">
                  <label class="ecfg-field__label">Encriptación</label>
                  <el-select
                    v-model="userConfigSmtp.encryption"
                    style="width:100%"
                  >
                    <el-option value="tls" label="TLS" />
                    <el-option value="ssl" label="SSL" />
                  </el-select>
                </el-form-item>
              </b-col>
            </b-row>
          </div>

          <div class="ecfg-section">
            <h6 class="ecfg-section__title">Información del remitente</h6>
            <b-row>
              <b-col xs="12" sm="12" md="6">
                <el-form-item class="ecfg-field">
                  <label class="ecfg-field__label">Nombre visible</label>
                  <el-input
                    type="text"
                    name="smtp-from-name"
                    autocomplete="off"
                    v-model="userConfigSmtp.from_name"
                    placeholder="Mi Empresa S.A."
                  />
                </el-form-item>
              </b-col>
              <b-col xs="12" sm="12" md="6">
                <el-form-item class="ecfg-field">
                  <label class="ecfg-field__label">Correo del remitente</label>
                  <el-input
                    type="text"
                    name="smtp-from-email"
                    autocomplete="off"
                    v-model="userConfigSmtp.from_email"
                    placeholder="info@empresa.com"
                  />
                </el-form-item>
              </b-col>
            </b-row>
          </div>

          <div class="ecfg-section ecfg-section--toggle">
            <div class="ecfg-toggle-row">
              <div>
                <span class="ecfg-toggle-label"
                  >Usar configuración personalizada de SMTP</span
                >
                <span class="ecfg-toggle-hint"
                  >Al activar esta opción, todos los correos se enviarán desde
                  tu servidor SMTP.</span
                >
              </div>
              <el-switch v-model="userConfigSmtp.active" />
            </div>
          </div>
        </el-form>
      </template>

      <!-- ── SMTP alterno para facturas ── -->
      <template v-if="activeTab === 'smtp_invoice'">
        <div class="email-cfg__header">
          <div class="email-cfg__header-info">
            <h5 class="email-cfg__title">Correo alterno para facturas</h5>
            <p class="email-cfg__desc">
              Opcional. Úsalo si otra persona o departamento maneja el envío de
              facturas con un correo distinto al SMTP general. Si lo dejas
              desactivado, las facturas se seguirán enviando tal cual como
              ahora.
            </p>
          </div>
          <ButtonSaveComponent @onSaveClick="handleSaveInvoiceClick" />
        </div>

        <el-form>
          <div class="ecfg-section">
            <h6 class="ecfg-section__title">Credenciales de acceso</h6>
            <b-row>
              <b-col xs="12" sm="12" md="6">
                <el-form-item class="ecfg-field">
                  <label class="ecfg-field__label"
                    >Usuario / Correo electrónico</label
                  >
                  <el-input
                    type="text"
                    name="smtp-invoice-username"
                    autocomplete="off"
                    :readonly="fieldsLocked"
                    @focus="fieldsLocked = false"
                    v-model="userConfigSmtpInvoice.email"
                    placeholder="facturacion@empresa.com"
                  />
                </el-form-item>
              </b-col>
              <b-col xs="12" sm="12" md="6">
                <el-form-item class="ecfg-field">
                  <label class="ecfg-field__label">Contraseña</label>
                  <el-input
                    type="password"
                    name="smtp-invoice-password"
                    autocomplete="new-password"
                    :readonly="fieldsLocked"
                    @focus="fieldsLocked = false"
                    v-model="userConfigSmtpInvoice.password"
                    show-password
                    placeholder="••••••••"
                  />
                </el-form-item>
              </b-col>
            </b-row>
          </div>

          <div class="ecfg-section">
            <h6 class="ecfg-section__title">Configuración del servidor</h6>
            <b-row>
              <b-col xs="12" sm="12" md="5">
                <el-form-item class="ecfg-field">
                  <label class="ecfg-field__label">Servidor SMTP</label>
                  <el-input
                    type="text"
                    name="smtp-invoice-host"
                    autocomplete="off"
                    :readonly="fieldsLocked"
                    @focus="fieldsLocked = false"
                    v-model="userConfigSmtpInvoice.host"
                    placeholder="smtp.gmail.com"
                  />
                  <span
                    :class="[
                      'ecfg-field__hint',
                      { 'ecfg-field__hint--error': isSmtpInvoiceHostInvalid }
                    ]"
                    >{{
                      isSmtpInvoiceHostInvalid
                        ? "Este campo debe ser el nombre del servidor (ej: smtp.gmail.com), no un correo electrónico."
                        : "Es el nombre del servidor (ej: smtp.gmail.com), no tu correo electrónico."
                    }}</span
                  >
                </el-form-item>
              </b-col>
              <b-col xs="12" sm="12" md="3">
                <el-form-item class="ecfg-field">
                  <label class="ecfg-field__label">Puerto</label>
                  <el-input
                    type="text"
                    name="smtp-invoice-port"
                    autocomplete="off"
                    v-model="userConfigSmtpInvoice.port"
                    placeholder="465"
                  />
                </el-form-item>
              </b-col>
              <b-col xs="12" sm="12" md="4">
                <el-form-item class="ecfg-field">
                  <label class="ecfg-field__label">Encriptación</label>
                  <el-select
                    v-model="userConfigSmtpInvoice.encryption"
                    style="width:100%"
                  >
                    <el-option value="tls" label="TLS" />
                    <el-option value="ssl" label="SSL" />
                  </el-select>
                </el-form-item>
              </b-col>
            </b-row>
          </div>

          <div class="ecfg-section">
            <h6 class="ecfg-section__title">Información del remitente</h6>
            <b-row>
              <b-col xs="12" sm="12" md="6">
                <el-form-item class="ecfg-field">
                  <label class="ecfg-field__label">Nombre visible</label>
                  <el-input
                    type="text"
                    name="smtp-invoice-from-name"
                    autocomplete="off"
                    v-model="userConfigSmtpInvoice.from_name"
                    placeholder="Mi Empresa S.A. - Facturación"
                  />
                </el-form-item>
              </b-col>
              <b-col xs="12" sm="12" md="6">
                <el-form-item class="ecfg-field">
                  <label class="ecfg-field__label">Correo del remitente</label>
                  <el-input
                    type="text"
                    name="smtp-invoice-from-email"
                    autocomplete="off"
                    v-model="userConfigSmtpInvoice.from_email"
                    placeholder="facturacion@empresa.com"
                  />
                </el-form-item>
              </b-col>
            </b-row>
          </div>

          <div class="ecfg-section ecfg-section--toggle">
            <div class="ecfg-toggle-row">
              <div>
                <span class="ecfg-toggle-label"
                  >Usar este correo para el envío de facturas</span
                >
                <span class="ecfg-toggle-hint"
                  >Al activar esta opción, las facturas se enviarán desde este
                  correo en vez del SMTP general.</span
                >
              </div>
              <el-switch v-model="userConfigSmtpInvoice.active" />
            </div>
          </div>

          <div class="ecfg-section ecfg-section--toggle">
            <div class="ecfg-toggle-row">
              <div>
                <span class="ecfg-toggle-label"
                  >Usar también para los recordatorios</span
                >
                <span class="ecfg-toggle-hint"
                  >Si lo activas, los recordatorios de pago de facturas y cuotas
                  también saldrán desde este correo en vez del principal.</span
                >
              </div>
              <el-switch
                v-model="userConfigSmtpInvoice.apply_to_reminders"
                :disabled="!userConfigSmtpInvoice.active"
              />
            </div>
          </div>
        </el-form>
      </template>

      <!-- ── Envíos masivos ── -->
      <template v-if="activeTab === 'resend'">
        <div class="email-cfg__header">
          <div class="email-cfg__header-info">
            <h5 class="email-cfg__title">Envíos masivos — Remitente</h5>
            <p class="email-cfg__desc">
              Nombre y correo de respuesta que verán tus clientes al recibir
              notificaciones masivas enviadas vía Resend.
            </p>
          </div>
          <ButtonSaveComponent @onSaveClick="handleSaveResendSender" />
        </div>

        <el-form>
          <div class="ecfg-section">
            <h6 class="ecfg-section__title">Configuración del remitente</h6>
            <b-row>
              <b-col xs="12" sm="12" md="6">
                <el-form-item class="ecfg-field">
                  <label class="ecfg-field__label"
                    >Nombre visible del remitente</label
                  >
                  <el-input
                    type="text"
                    v-model="resendSender.from_name"
                    placeholder="Mi Empresa S.A."
                  />
                </el-form-item>
              </b-col>
              <b-col xs="12" sm="12" md="6">
                <el-form-item class="ecfg-field">
                  <label class="ecfg-field__label"
                    >Correo de respuesta (reply-to)</label
                  >
                  <el-input
                    type="text"
                    v-model="resendSender.reply_to"
                    placeholder="info@empresa.com"
                  />
                </el-form-item>
              </b-col>
            </b-row>
          </div>
        </el-form>
      </template>
    </div>
  </div>
</template>

<script>
import configService from "../../services/configService";
import ButtonSaveComponent from "../../../components/ButtonSave";

export default {
  name: "PreferencesEmailComponent",
  data() {
    return {
      activeTab: "smtp",
      tabs: [
        { key: "smtp", label: "Servidor SMTP", icon: "el-icon-s-promotion" },
        {
          key: "smtp_invoice",
          label: "Correo de facturas",
          icon: "el-icon-tickets"
        },
        { key: "resend", label: "Envíos masivos", icon: "el-icon-message" }
      ],
      // Los campos de credenciales arrancan readonly y se desbloquean al primer
      // foco: Chrome no inyecta contraseñas guardadas en inputs readonly al
      // cargar la página (autocomplete="off" no basta para el gestor de claves).
      fieldsLocked: true,
      userConfigSmtp: {},
      userConfigSmtpInvoice: {},
      resendSender: { from_name: "", reply_to: "" }
    };
  },
  computed: {
    isSmtpHostInvalid() {
      const host = this.userConfigSmtp.host;
      return !!host && host.includes("@");
    },
    isSmtpInvoiceHostInvalid() {
      const host = this.userConfigSmtpInvoice.host;
      return !!host && host.includes("@");
    }
  },
  methods: {
    async handleGetUserConfigSmtp() {
      this.$store.dispatch("toggleRequestLoading");
      try {
        const businessId = this.$store.getters.getUser?.user_business?.id;
        const response = await configService
          .getUserConfigSmtp(businessId)
          .finally(() => {
            this.$store.dispatch("toggleRequestLoading");
          });
        const { data } = response.data;
        if (data) this.userConfigSmtp = data;
      } catch {
        return false;
      }
    },
    async handleSaveClick() {
      if (this.isSmtpHostInvalid) {
        this.$notifications.error({
          message:
            "El campo Servidor SMTP debe ser el nombre del servidor (ej: smtp.gmail.com), no un correo electrónico."
        });
        return false;
      }
      this.$store.dispatch("toggleRequestLoading");
      try {
        const businessId = this.$store.getters.getUser?.user_business?.id;
        const response = await configService
          .updateUserConfigSmtp(this.userConfigSmtp, businessId)
          .finally(() => {
            this.$store.dispatch("toggleRequestLoading");
          });
        const { success, message } = response.data;
        if (success) this.$notifications.success({ message });
      } catch {
        return false;
      }
    },
    async handleGetUserConfigSmtpInvoice() {
      this.$store.dispatch("toggleRequestLoading");
      try {
        const businessId = this.$store.getters.getUser?.user_business?.id;
        const response = await configService
          .getUserConfigSmtpInvoice(businessId)
          .finally(() => {
            this.$store.dispatch("toggleRequestLoading");
          });
        const { data } = response.data;
        if (data) this.userConfigSmtpInvoice = data;
      } catch {
        return false;
      }
    },
    async handleSaveInvoiceClick() {
      if (this.isSmtpInvoiceHostInvalid) {
        this.$notifications.error({
          message:
            "El campo Servidor SMTP debe ser el nombre del servidor (ej: smtp.gmail.com), no un correo electrónico."
        });
        return false;
      }
      this.$store.dispatch("toggleRequestLoading");
      try {
        const businessId = this.$store.getters.getUser?.user_business?.id;
        const response = await configService
          .updateUserConfigSmtpInvoice(this.userConfigSmtpInvoice, businessId)
          .finally(() => {
            this.$store.dispatch("toggleRequestLoading");
          });
        const { success, message } = response.data;
        if (success) this.$notifications.success({ message });
      } catch {
        return false;
      }
    },
    async handleGetResendSender() {
      try {
        const response = await configService.getResendSender();
        const { data } = response.data;
        if (data)
          this.resendSender = {
            from_name: data.from_name || "",
            reply_to: data.reply_to || ""
          };
      } catch {
        return false;
      }
    },
    async handleSaveResendSender() {
      this.$store.dispatch("toggleRequestLoading");
      try {
        const response = await configService
          .updateResendSender(this.resendSender)
          .finally(() => {
            this.$store.dispatch("toggleRequestLoading");
          });
        const { success, message } = response.data;
        if (success) this.$notifications.success({ message });
      } catch {
        return false;
      }
    }
  },
  components: { ButtonSaveComponent },
  mounted() {
    this.handleGetUserConfigSmtp();
    this.handleGetUserConfigSmtpInvoice();
    this.handleGetResendSender();
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$accent: #3ecfb4;
$border: #e4eaf6;
$bg: #f8f9fc;
$muted: #909399;

// ── Two-column layout ──
.email-cfg {
  display: flex;
  gap: 0;
  min-height: 400px;
}

// ── Left sidebar ──
.email-cfg__nav {
  width: 200px;
  flex-shrink: 0;
  border-right: 1px solid $border;
  padding: 4px 0;
}

.email-cfg__nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 11px 18px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: $muted;
  text-align: left;
  border-radius: 0;
  transition: all 0.15s;
  border-left: 3px solid transparent;

  i {
    font-size: 15px;
  }

  &:hover {
    background: $bg;
    color: $primary;
  }

  &.active {
    background: #f0faf8;
    color: $primary;
    font-weight: 700;
    border-left-color: $accent;
  }
}

// ── Right content ──
.email-cfg__body {
  flex: 1;
  padding: 0 0 0 28px;
  min-width: 0;
}

// ── Section header (title + save button) ──
.email-cfg__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid $border;
}

.email-cfg__header-info {
  flex: 1;
  min-width: 0;
}

.email-cfg__title {
  font-size: 15px;
  font-weight: 700;
  color: $primary;
  margin: 0 0 4px;
}

.email-cfg__desc {
  font-size: 13px;
  color: $muted;
  margin: 0;
  line-height: 1.5;
}

// ── Form sections ──
.ecfg-section {
  margin-bottom: 24px;
  padding: 18px 20px;
  background: $bg;
  border: 1px solid $border;
  border-radius: 12px;

  &--toggle {
    background: #fff;
  }
}

.ecfg-section__title {
  font-size: 13px;
  font-weight: 700;
  color: $primary;
  margin: 0 0 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid $border;
}

.ecfg-field {
  margin-bottom: 0 !important;
}

.ecfg-field__label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: $primary;
  margin: 0 0 6px;
}

.ecfg-field__hint {
  display: block;
  font-size: 11px;
  color: $muted;
  line-height: 1.4;
  margin-top: 4px;
}

.ecfg-field__hint--error {
  color: #f56c6c;
}

::v-deep .ecfg-section .el-input__inner,
::v-deep .ecfg-section .el-select .el-input__inner {
  color: $primary;
}

// ── Toggle row ──
.ecfg-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  span {
    display: block;
  }
}

.ecfg-toggle-label {
  font-size: 14px;
  font-weight: 600;
  color: $primary;
  margin-bottom: 3px;
}

.ecfg-toggle-hint {
  font-size: 12px;
  color: $muted;
  line-height: 1.5;
}

// ── Responsive: stack on mobile ──
@media (max-width: 600px) {
  .email-cfg {
    flex-direction: column;
  }
  .email-cfg__nav {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid $border;
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    padding: 0;
  }
  .email-cfg__nav-item {
    border-left: none;
    border-bottom: 3px solid transparent;
    white-space: nowrap;
    &.active {
      border-bottom-color: $accent;
      border-left-color: transparent;
    }
  }
  .email-cfg__body {
    padding: 20px 0 0;
  }
  .email-cfg__header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  .email-cfg__desc {
    display: none;
  }
  .email-cfg__title {
    font-size: 14px;
  }
}
</style>
